import { vec3 } from 'gl-matrix'
import GLArrayBuffer from 'nanogl/arraybuffer'
import GLIndexBuffer from 'nanogl/indexbuffer'
import Program from 'nanogl/program'
import { GLContext } from 'nanogl/types'
import Accessor from 'nanogl-gltf/lib/elements/Accessor'
import Node from 'nanogl-gltf/lib/elements/Node'
import Primitive from 'nanogl-gltf/lib/elements/Primitive'
import Texture from 'nanogl-gltf/lib/elements/Texture'
import Gltf from 'nanogl-gltf/lib/Gltf'
import { GltfLoaderOptions } from 'nanogl-gltf/lib/io/GltfLoaderOptions'
import MeshRenderer from 'nanogl-gltf/lib/renderer/MeshRenderer'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'
import Bounds from 'nanogl-pbr/Bounds'
import GLState, { LocalConfig } from 'nanogl-state/GLState'

import { RenderContext } from '@/webgl/core/RenderContext'
import GltfResource from '@/webgl/resources/GltfResource'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'

type ParticleSource = {
  vertices: Float32Array
  texcoords: Float32Array
  indices: Uint16Array
}

export type ParticleData = {
  position: vec3
  velocity: vec3
  scale: number
  rotation: number
  speed: number
  angularSpeed: number
  lifetime: number
  delay: number
}

export default class GltfParticles implements IRenderable, ILoadable {
  node: Node
  gltf: Gltf

  renderer: MeshRenderer
  primitive: Primitive
  program: Program
  cfg: LocalConfig
  texture: Texture

  sources: ParticleSource[] = []
  bounds: Bounds

  constructor(
    private readonly gl: GLContext,
    private readonly request: string,
    private readonly opts?: GltfLoaderOptions
  ) {
    this.program = new Program(gl)
    this.cfg = GLState.get(gl).config()
    this.bounds = new Bounds()
  }

  async load(abort?: AbortSignal): Promise<GltfParticles> {
    this.gltf = await new GltfResource(this.request, this.gl, this.opts).load()

    const meshes = this.gltf.renderables.map((r) => r.mesh)

    this.texture = this.gltf.getElement(GltfTypes.TEXTURE, 0)

    for (let i = 0; i < meshes.length; i++) {
      const mesh = meshes[i]
      const primitive = mesh.primitives[0]

      const paccess: Accessor = primitive.attributes.getSemantic('POSITION').accessor
      const uvaccess: Accessor = primitive.attributes.getSemantic('TEXCOORD_0').accessor

      const vertices: Float32Array = new Float32Array(paccess.count * 3)
      const texcoords: Float32Array = new Float32Array(uvaccess.count * 2)
      const indices: Uint16Array = new Uint16Array(primitive.indices.count)

      paccess.getValues(vertices, 0, paccess.count * 3)
      uvaccess.getValues(texcoords, 0, uvaccess.count * 2)
      primitive.indices.getValues(indices, 0, primitive.indices.count)

      this.sources.push({ vertices, texcoords, indices })
    }
    this.node = this.gltf.root as Node

    return this
  }

  generate(particles: ParticleData[]) {
    const vertices: number[] = []
    const texcoords: number[] = []
    const indices: number[] = []
    const positions: number[] = []
    const velocities: number[] = []
    const settings: number[] = []

    let indexoffset = 0
    for (let i = 0; i < particles.length; i++) {
      const particle = particles[i]
      const source = this.sources[Math.floor(Math.random() * this.sources.length)]
      const vcount = source.vertices.length / 3
      for (let j = 0; j < vcount; j++) {
        positions.push(...particle.position, particle.rotation)
        velocities.push(...particle.velocity, particle.speed)
        settings.push(particle.scale, particle.angularSpeed, particle.lifetime, particle.delay)
      }

      vertices.push(...source.vertices)
      texcoords.push(...source.texcoords)
      indices.push(...source.indices.map((v) => v + indexoffset))
      indexoffset += vcount
    }

    const indexBuffer = new GLIndexBuffer(this.gl, this.gl.UNSIGNED_SHORT)
    indexBuffer.data(new Uint16Array(indices))

    const vertexBuffer = new GLArrayBuffer(this.gl)
    vertexBuffer.data(new Float32Array(vertices))
    vertexBuffer.attrib('aPosition', 3, this.gl.FLOAT)

    const uvBuffer = new GLArrayBuffer(this.gl)
    uvBuffer.data(new Float32Array(texcoords))
    uvBuffer.attrib('aTexCoord', 2, this.gl.FLOAT)

    const settingsBuffer = new GLArrayBuffer(this.gl)
    settingsBuffer.data(new Float32Array(settings))
    settingsBuffer.attrib('aSettings', 4, this.gl.FLOAT)

    const positionsBuffer = new GLArrayBuffer(this.gl)
    positionsBuffer.data(new Float32Array(positions))
    positionsBuffer.attrib('aTransform', 4, this.gl.FLOAT)

    const directionBuffer = new GLArrayBuffer(this.gl)
    directionBuffer.data(new Float32Array(velocities))
    directionBuffer.attrib('aVelocity', 4, this.gl.FLOAT)

    this.primitive = new Primitive()
    this.primitive._vaoMap = new Map()
    this.primitive.indexBuffer = indexBuffer
    this.primitive.buffers = [vertexBuffer, uvBuffer, settingsBuffer, positionsBuffer, directionBuffer]
  }

  preRender(dt: number): void {}

  getBounds(): Bounds {
    return this.bounds
  }

  render(context: RenderContext): void {
    this.program.use()
    this.program.uMVP(context.camera.getMVP(this.node._wmatrix))
    this.program.uBaseColor(this.texture.glTexture)

    this.cfg.apply()
    this.primitive.getVao(this.program).bind()
    this.primitive.indexBuffer.bind()
    this.primitive.indexBuffer.drawTriangles()
    this.primitive.getVao(this.program).unbind()
  }
}
